import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import { portalService } from "apiServices";
import { Buttons, Icons, Modals } from "components";
import { SerialImportStyled, SerialImportModalStyled } from "./styled";

const TEMPLATE_HEADER = ["SerialNo", "ItemNo"];

// ใส่ BOM ให้ Excel เปิดภาษาไทยได้
const downloadCsv = (fileName, rows) => {
  const content = rows
    .map((row) =>
      row.map((e) => `"${String(e === undefined || e === null ? "" : e).replace(/"/g, '""')}"`).join(",")
    )
    .join("\r\n");
  const blob = new Blob([`﻿${content}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

const parseCsvLine = (line) => {
  const result = [];
  let current = "";
  let inQuote = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuote && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuote = !inQuote;
      }
    } else if (char === "," && !inQuote) {
      result.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  result.push(current);
  return result.map((e) => e.trim());
};

// คอลัมน์ SerialNo, ItemNo ตาม Template แถวหัวตารางข้ามได้
const parseSerialCsv = (text) => {
  const lines = text
    .replace(/^﻿/, "")
    .split(/\r?\n/)
    .filter((e) => e.trim());
  if (lines.length && /serial/i.test(lines[0])) lines.shift();
  return lines.map((line) => {
    const [serial_no, item_no] = parseCsvLine(line);
    return { serial_no: serial_no || "", item_no: item_no || "" };
  });
};

const SerialImport = ({ setId, existingIds = [], disabled, onAdd }) => {
  const inputRef = useRef(null);
  const [_fileName, _setFileName] = useState("");
  const [_isChecking, _setIsChecking] = useState(false);
  const [_result, _setResult] = useState(null);

  const _handleChangeFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      toast.error("รองรับเฉพาะไฟล์ .csv ตาม Template");
      return;
    }
    _setFileName(file.name);
    const reader = new FileReader();
    reader.onload = async () => {
      const items = parseSerialCsv(String(reader.result || ""));
      if (items.length === 0) {
        toast.error("ไม่พบรายการในไฟล์");
        return;
      }
      _setIsChecking(true);
      const res = await portalService.POST_CORPERATE_PRODUCT_SET_CHECK_SERIAL({
        items,
        product_warranty_set_id: setId || null,
      });
      _setIsChecking(false);
      if (res && res.status === 200) {
        const existing = new Set(existingIds.map(String));
        _setResult(
          res.data.map((row) =>
            row.is_valid && existing.has(String(row.product_warranty_id))
              ? { ...row, is_valid: false, reason: "มีอยู่ในรายการของ Set แล้ว" }
              : row
          )
        );
      } else {
        toast.error(res?.data?.error?.message || "ตรวจสอบไฟล์ไม่สำเร็จ");
      }
    };
    reader.readAsText(file);
  };

  const _validList = (_result || []).filter((e) => e.is_valid);
  const _invalidList = (_result || []).filter((e) => !e.is_valid);

  const _handleDownloadInvalid = () => {
    downloadCsv("serial_ใช้ไม่ได้.csv", [
      [...TEMPLATE_HEADER, "เหตุผล"],
      ..._invalidList.map((e) => [e.serial_no, e.item_no, e.reason]),
    ]);
  };

  const _handleAdd = () => {
    onAdd(_validList);
    _setResult(null);
  };

  return (
    <SerialImportStyled>
      <div className="si_row">
        <div className="si_file">{_fileName || "No file Chosen"}</div>
        <div className="si_btn">
          <Buttons.BgStandard
            theme_gray
            label={_isChecking ? "กำลังตรวจสอบ..." : "Browse"}
            disabled={disabled || _isChecking}
            onClick={() => inputRef.current && inputRef.current.click()}
          />
        </div>
        <div className="si_btn">
          <Buttons.BgStandard
            label="Download Template"
            onClick={() =>
              downloadCsv("corperate_set_serial_template.csv", [TEMPLATE_HEADER])
            }
          />
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".csv"
          hidden
          onChange={_handleChangeFile}
        />
      </div>
      <div className="si_desc">*** ไฟล์ .csv คอลัมน์ SerialNo, ItemNo ตาม Template</div>
      <Modals.BodyEmpty
        isShowModal={_result ? true : false}
        onCloseModal={() => _setResult(null)}
      >
        <SerialImportModalStyled>
          <div className="btn_close_modal" onClick={() => _setResult(null)}>
            <Icons.CloseX />
          </div>
          <div className="modal_title">ผลการตรวจสอบไฟล์ {_fileName}</div>
          <div className="summary">
            ใช้ได้ <span className="txt_green">{_validList.length}</span> รายการ,
            ใช้ไม่ได้ <span className="txt_red">{_invalidList.length}</span> รายการ
          </div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>SerialNo</th>
                <th>ItemNo</th>
                <th>ผลตรวจสอบ</th>
              </tr>
            </thead>
            <tbody>
              {(_result || []).map((e, i) => (
                <tr key={i} className={e.is_valid ? "" : "row_invalid"}>
                  <td>{i + 1}</td>
                  <td>{e.serial_no || "-"}</td>
                  <td>{e.item_no || "-"}</td>
                  <td>{e.is_valid ? "ใช้ได้" : e.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="modal_action">
            {_invalidList.length > 0 && (
              <div className="w220">
                <Buttons.BgStandard
                  theme_red
                  label="Download รายการที่ใช้ไม่ได้"
                  onClick={_handleDownloadInvalid}
                />
              </div>
            )}
            <div className="w220">
              <Buttons.BgStandard
                theme_green
                label={`เพิ่มรายการที่ใช้ได้ (${_validList.length})`}
                disabled={_validList.length === 0}
                onClick={_handleAdd}
              />
            </div>
          </div>
        </SerialImportModalStyled>
      </Modals.BodyEmpty>
    </SerialImportStyled>
  );
};

export default SerialImport;
